import { Modal, Select, Button, Space, Typography, message, Spin } from 'antd';
import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SlackApiService } from '@src/api';
import type { Channel, MappingResponse } from '@src/api';

const { Title, Text } = Typography;
const { Option } = Select;

interface SlackSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

const ALERT_TYPES = {
  AI_DETECT_REALTIME: {
    key: 'AI_DETECT_REALTIME',
    label: 'AI 실시간 감지 알림',
    color: '#4403B5',
  },
  AI_INSIGHT: {
    key: 'AI_INSIGHT',
    label: 'AI 인사이트 알림',
    color: '#4403B5',
  },
  AI_REPORT_SUMMARY: {
    key: 'AI_REPORT_SUMMARY',
    label: 'AI 종합리포트',
    color: '#4403B5',
  },
};

const SlackSettingsModal: React.FC<SlackSettingsModalProps> = ({
  visible,
  onClose,
}) => {
  const [selectedChannels, setSelectedChannels] = useState<
    Record<string, string>
  >({
    AI_DETECT_REALTIME: '',
    AI_INSIGHT: '',
    AI_REPORT_SUMMARY: '',
  });

  // 슬랙 채널 목록 조회
  const { data: channelsData, isLoading: channelsLoading } = useQuery(
    ['slackChannels'],
    () => SlackApiService.getChannels(),
    {
      enabled: visible,
      suspense: false,
      onError: () => {
        message.error('슬랙 채널 목록을 불러올 수 없습니다.');
      },
    },
  );

  // 현재 매핑 설정 조회
  const { data: mappingsData, isLoading: mappingsLoading } = useQuery(
    ['slackMappings'],
    () => SlackApiService.getMappings(),
    {
      enabled: visible,
      suspense: false,
      onError: () => {
        message.error('알림 설정을 불러올 수 없습니다.');
      },
    },
  );

  // 매핑 설정 업데이트 뮤테이션
  const updateMappingMutation = useMutation(
    (mapping: { alertType: string; targetId: string; targetName: string }) =>
      SlackApiService.createOrUpdateMapping({
        requestBody: {
          alertType: mapping.alertType as any,
          targetType: 'CHANNEL',
          targetId: mapping.targetId,
          targetName: mapping.targetName,
        },
      }),
    {
      onError: () => {
        message.error('알림 설정 저장에 실패했습니다.');
      },
    },
  );

  // 매핑 삭제 뮤테이션
  const deleteMappingMutation = useMutation(
    (mappingId: string) => SlackApiService.deleteMapping({ mappingId }),
    {
      onError: () => {
        message.error('알림 설정 삭제에 실패했습니다.');
      },
    },
  );

  // 테스트 메시지 전송 뮤테이션
  const sendTestMutation = useMutation(
    ({
      alertType,
      targetId,
      targetName,
    }: {
      alertType: string;
      targetId: string;
      targetName: string;
    }) =>
      SlackApiService.sendTestMessage({
        requestBody: {
          alertType: alertType as any,
          targetType: 'CHANNEL',
          targetId,
          targetName,
        },
      }),
    {
      onSuccess: () => {
        message.success('테스트 메시지가 전송되었습니다.');
      },
      onError: () => {
        message.error('테스트 메시지 전송에 실패했습니다.');
      },
    },
  );

  // 매핑 데이터로 초기값 설정
  useEffect(() => {
    if (mappingsData?.data?.mappings) {
      const mappings = mappingsData.data.mappings;
      const newSelectedChannels: Record<string, string> = {
        AI_DETECT_REALTIME: '',
        AI_INSIGHT: '',
        AI_REPORT_SUMMARY: '',
      };

      mappings.forEach((mapping: MappingResponse) => {
        if (mapping.alertType && mapping.targetId) {
          newSelectedChannels[mapping.alertType] = mapping.targetId;
        }
      });

      setSelectedChannels(newSelectedChannels);
    }
  }, [mappingsData]);

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!visible) {
      // 모달이 닫히면 서버에서 받은 원래 값으로 리셋
      if (mappingsData?.data?.mappings) {
        const mappings = mappingsData.data.mappings;
        const newSelectedChannels: Record<string, string> = {
          AI_DETECT_REALTIME: '',
          AI_INSIGHT: '',
          AI_REPORT_SUMMARY: '',
        };

        mappings.forEach((mapping: MappingResponse) => {
          if (mapping.alertType && mapping.targetId) {
            newSelectedChannels[mapping.alertType] = mapping.targetId;
          }
        });

        setSelectedChannels(newSelectedChannels);
      } else {
        // 매핑 데이터가 없으면 빈 값으로 초기화
        setSelectedChannels({
          AI_DETECT_REALTIME: '',
          AI_INSIGHT: '',
          AI_REPORT_SUMMARY: '',
        });
      }
    }
  }, [visible, mappingsData]);

  const handleChannelChange = (alertType: string, channelId: string) => {
    setSelectedChannels((prev) => ({
      ...prev,
      [alertType]: channelId,
    }));
  };

  const handleTestSend = (alertType: string) => {
    if (!selectedChannels[alertType]) {
      message.warning('채널을 먼저 선택해주세요.');
      return;
    }

    const alertTypeConfig = ALERT_TYPES[alertType as keyof typeof ALERT_TYPES];

    sendTestMutation.mutate({
      alertType,
      targetId: selectedChannels[alertType],
      targetName: alertTypeConfig?.label || '',
    });
  };

  const handleSave = async () => {
    const channels = channelsData?.data?.channels || [];
    const currentMappings = mappingsData?.data?.mappings || [];

    try {
      const promises: Promise<any>[] = [];

      // 각 알림 타입별로 처리
      Object.entries(selectedChannels).forEach(([alertType, channelId]) => {
        const existingMapping = currentMappings.find(
          (mapping: MappingResponse) => mapping.alertType === alertType,
        );

        if (channelId) {
          // 채널이 선택된 경우: 생성 또는 업데이트
          const channel = channels.find((ch: Channel) => ch.id === channelId);
          promises.push(
            updateMappingMutation.mutateAsync({
              alertType,
              targetId: channelId,
              targetName: channel?.name || '',
            }),
          );
        } else if (existingMapping?.id) {
          // 채널이 선택되지 않았고 기존 매핑이 있는 경우: 삭제
          promises.push(deleteMappingMutation.mutateAsync(existingMapping.id));
        }
      });

      await Promise.all(promises);
      message.success('알림 설정이 저장되었습니다.');
      onClose();
    } catch (error) {
      // 에러는 mutation에서 처리
    }
  };

  const isLoading = channelsLoading || mappingsLoading;
  const channels = channelsData?.data?.channels || [];

  return (
    <Modal
      title={
        <div style={{ fontSize: '18px', fontWeight: 600 }}>
          슬랙에서 바인더 메시지를 받아보세요!
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleSave}
          style={{
            backgroundColor: '#4403B5',
            borderColor: '#4403B5',
          }}
        >
          확인
        </Button>,
      ]}
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {Object.entries(ALERT_TYPES).map(([key, config]) => (
            <div key={key}>
              <Text strong style={{ color: config.color, fontSize: '16px' }}>
                {config.label}
              </Text>
              <div
                style={{
                  marginTop: '8px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <Select
                  placeholder="채널을 선택하세요."
                  style={{ flex: 1, minWidth: '200px' }}
                  value={selectedChannels[key] || undefined}
                  onChange={(value) => handleChannelChange(key, value)}
                  allowClear
                >
                  {channels.map((channel: Channel) => (
                    <Option key={channel.id} value={channel.id || ''}>
                      {channel.isPrivate ? '🔒 ' : '# '}
                      {channel.name}
                    </Option>
                  ))}
                </Select>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: 'auto' }}>
                  <Button
                    onClick={() => handleChannelChange(key, '')}
                    disabled={!selectedChannels[key]}
                    style={{ minWidth: '60px' }}
                  >
                    해제
                  </Button>
                  <Button
                    onClick={() => handleTestSend(key)}
                    disabled={!selectedChannels[key]}
                    style={{ minWidth: '80px' }}
                  >
                    테스트 발송
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </Space>
      )}
    </Modal>
  );
};

export default SlackSettingsModal;
